import React, { useState, useEffect } from 'react';
import { pluginService } from '../../lib/pluginService';
import { supabase } from '../../lib/supabase';
import { USE_MOCK_DATA, mockClients, mockPlugins } from '../../lib/mockData';
import {
  Plug,
  X,
  RefreshCw,
  Settings,
  Trash2,
  Plus,
  AlertCircle,
  Activity,
  Clock,
  TrendingUp,
  CheckCircle,
  XCircle,
  History,
  Zap
} from 'lucide-react';

interface Plugin {
  id: string;
  name: string;
  type: string;
  icon: string;
  description: string;
  is_active: boolean;
  config_schema: {
    fields: Array<{
      name: string;
      type: string;
      label: string;
      required: boolean;
    }>;
  };
}

interface ClientPlugin {
  id: string;
  client_id: string;
  plugin_id: string;
  is_connected: boolean;
  sync_status: 'active' | 'error' | 'disconnected';
  error_message: string | null;
  last_sync_at: string | null;
  settings: any;
  plugin: Plugin;
  total_syncs?: number;
  failed_syncs?: number;
  sync_frequency?: string;
  auto_sync_enabled?: boolean;
  next_sync_at?: string | null;
}

interface Client {
  id: string;
  company_name: string;
  email: string;
}

const PluginManagerEnhanced = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [clientPlugins, setClientPlugins] = useState<ClientPlugin[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState<Plugin | null>(null);
  const [selectedClientPlugin, setSelectedClientPlugin] = useState<ClientPlugin | null>(null);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [settings, setSettings] = useState({
    syncFrequency: 'hourly',
    autoSyncEnabled: true
  });
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadClients();
    loadPlugins();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadClientPlugins(selectedClient);
    }
  }, [selectedClient]);

  const loadClients = async () => {
    if (USE_MOCK_DATA) {
      setClients(mockClients as Client[]);
      if (mockClients.length > 0) {
        setSelectedClient(mockClients[0].id);
      }
      return;
    }

    const { data, error } = await supabase
      .from('clients')
      .select('id, company_name, email')
      .order('company_name');

    if (error) {
      console.error('Error loading clients:', error);
      setClients(mockClients as Client[]);
      if (mockClients.length > 0) {
        setSelectedClient(mockClients[0].id);
      }
    } else {
      setClients(data || []);
      if (data && data.length > 0) {
        setSelectedClient(data[0].id);
      }
    }
  };

  const loadPlugins = async () => {
    if (USE_MOCK_DATA) {
      setPlugins(mockPlugins as Plugin[]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('plugins')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) {
      console.error('Error loading plugins:', error);
      setPlugins(mockPlugins as Plugin[]);
    } else {
      setPlugins(data || []);
    }
    setLoading(false);
  };

  const loadClientPlugins = async (clientId: string) => {
    setLoading(true);

    if (USE_MOCK_DATA) {
      const { mockClientPlugins } = await import('../../lib/mockData');
      const clientPluginsForClient = mockClientPlugins
        .filter(cp => cp.client_id === clientId)
        .map(cp => {
          const plugin = mockPlugins.find(p => p.id === cp.plugin_id);
          return {
            id: cp.plugin_id + '_' + cp.client_id,
            ...cp,
            total_syncs: Math.floor(Math.random() * 100) + 20,
            failed_syncs: Math.floor(Math.random() * 5),
            sync_frequency: 'hourly',
            auto_sync_enabled: true,
            next_sync_at: new Date(Date.now() + 3600000).toISOString(),
            plugin: {
              ...plugin,
              description: `${plugin?.name} integration for social media`,
              is_active: true,
              config_schema: {
                fields: [
                  { name: 'api_key', type: 'password', label: 'API Key', required: true },
                  { name: 'api_secret', type: 'password', label: 'API Secret', required: true }
                ]
              }
            }
          } as ClientPlugin;
        });

      setClientPlugins(clientPluginsForClient);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('client_plugins')
      .select(`
        *,
        plugin:plugins(*)
      `)
      .eq('client_id', clientId)
      .eq('is_connected', true);

    if (error) {
      console.error('Error loading client plugins:', error);
      setError('Failed to load client plugins');
    } else {
      setClientPlugins(data || []);
    }
    setLoading(false);
  };

  const handleConnectPlugin = (plugin: Plugin) => {
    setSelectedPlugin(plugin);
    setCredentials({});
    setSettings({ syncFrequency: 'hourly', autoSyncEnabled: true });
    setShowConnectModal(true);
    setError(null);
  };

  const handleSaveConnection = async () => {
    if (!selectedPlugin || !selectedClient) return;

    const requiredFields = selectedPlugin.config_schema.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !credentials[f.name]);

    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await pluginService.connectPlugin({
      pluginId: selectedPlugin.id,
      clientId: selectedClient,
      credentials,
      settings
    });

    if (result.success) {
      await loadClientPlugins(selectedClient);
      setShowConnectModal(false);
      setCredentials({});
      setSuccess(`${selectedPlugin.name} connected successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError('Failed to connect plugin: ' + result.error);
    }
    setLoading(false);
  };

  const handleDisconnectPlugin = async (clientPlugin: ClientPlugin) => {
    if (!confirm(`Are you sure you want to disconnect ${clientPlugin.plugin.name}?`)) return;

    setLoading(true);
    const result = await pluginService.disconnectPlugin(clientPlugin.id);

    if (result.success) {
      await loadClientPlugins(selectedClient);
      setSuccess('Plugin disconnected successfully');
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError('Failed to disconnect plugin');
    }
    setLoading(false);
  };

  const handleSyncPlugin = async (clientPlugin: ClientPlugin) => {
    setSyncing(clientPlugin.id);
    setError(null);

    const result = await pluginService.syncPlugin(clientPlugin.id, { syncType: 'manual' });

    if (result.success) {
      await loadClientPlugins(selectedClient);
      setSuccess(`Synced ${result.data?.items_synced || 0} items from ${clientPlugin.plugin.name}`);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(`Sync failed: ${result.error}`);
    }

    setSyncing(null);
  };

  const handleOpenSettings = (clientPlugin: ClientPlugin) => {
    setSelectedClientPlugin(clientPlugin);
    setSettings({
      syncFrequency: clientPlugin.sync_frequency || 'hourly',
      autoSyncEnabled: clientPlugin.auto_sync_enabled !== false
    });
    setShowSettingsModal(true);
  };

  const handleSaveSettings = async () => {
    if (!selectedClientPlugin) return;

    setLoading(true);
    const result = await pluginService.updatePluginSettings(selectedClientPlugin.id, settings);

    if (result.success) {
      await loadClientPlugins(selectedClient);
      setShowSettingsModal(false);
      setSuccess('Settings updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError('Failed to update settings');
    }
    setLoading(false);
  };

  const handleViewHistory = async (clientPlugin: ClientPlugin) => {
    setSelectedClientPlugin(clientPlugin);
    setShowHistoryModal(true);

    const result = await pluginService.getSyncHistory(clientPlugin.id, 20);
    if (result.success) {
      setSyncHistory(result.data || []);
    }
  };

  const getPlatformLogo = (iconName: string) => {
    const logoMap: Record<string, { bg: string; logo: JSX.Element }> = {
      facebook: {
        bg: 'bg-blue-600',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        )
      },
      instagram: {
        bg: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        )
      },
      twitter: {
        bg: 'bg-black',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        )
      },
      linkedin: {
        bg: 'bg-blue-700',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        )
      },
      youtube: {
        bg: 'bg-red-600',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        )
      },
      tiktok: {
        bg: 'bg-black',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
          </svg>
        )
      },
      whatsapp: {
        bg: 'bg-green-500',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
        )
      },
      pinterest: {
        bg: 'bg-red-700',
        logo: (
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
          </svg>
        )
      },
      snapchat: {
        bg: 'bg-yellow-400',
        logo: (
          <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.076.346.149.675.254.975.299.18 0 .31-.044.399-.09-.016-.14-.031-.29-.044-.479-.014-.18-.014-.362-.014-.553 0-1.482-.184-3.007.335-4.334.6-1.378 1.918-2.347 3.825-2.817.675-.165 1.452-.27 2.411-.285.045-.015.09-.015.149-.015z"/>
          </svg>
        )
      }
    };

    const defaultLogo = {
      bg: 'bg-gray-600',
      logo: <Plug className="w-6 h-6 text-white" />
    };

    return logoMap[iconName.toLowerCase()] || defaultLogo;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'disconnected': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSuccessRate = (cp: ClientPlugin) => {
    if (!cp.total_syncs || cp.total_syncs === 0) return 100;
    return Math.round(((cp.total_syncs - (cp.failed_syncs || 0)) / cp.total_syncs) * 100);
  };

  const formatNextSync = (nextSync: string | null | undefined) => {
    if (!nextSync) return 'Not scheduled';
    const date = new Date(nextSync);
    const now = new Date();
    const diff = date.getTime() - now.getTime();

    if (diff < 0) return 'Due now';
    if (diff < 3600000) return `in ${Math.round(diff / 60000)} min`;
    if (diff < 86400000) return `in ${Math.round(diff / 3600000)} hr`;
    return `in ${Math.round(diff / 86400000)} days`;
  };

  const connectedPluginIds = clientPlugins.map(cp => cp.plugin_id);
  const availablePlugins = plugins.filter(p => !connectedPluginIds.includes(p.id));

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Plugin Manager</h1>
        <p className="text-gray-600">Connect and manage social media platform integrations for clients</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Client
        </label>
        <select
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {clients.map(client => (
            <option key={client.id} value={client.id}>
              {client.company_name}
            </option>
          ))}
        </select>
      </div>

      {selectedClient && (
        <>
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Connected Plugins</h2>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : clientPlugins.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clientPlugins.map(cp => {
                  const successRate = getSuccessRate(cp);
                  const isHealthy = successRate >= 90;

                  return (
                    <div
                      key={cp.id}
                      className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 ${getPlatformLogo(cp.plugin.icon).bg} rounded-lg flex items-center justify-center shadow-md`}>
                            {getPlatformLogo(cp.plugin.icon).logo}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{cp.plugin.name}</h3>
                            <p className="text-xs text-gray-600">{cp.plugin.description}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Status:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(cp.sync_status)}`}>
                            {cp.sync_status}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Health:</span>
                          <div className="flex items-center gap-1">
                            {isHealthy ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-orange-600" />
                            )}
                            <span className={`text-xs font-medium ${isHealthy ? 'text-green-600' : 'text-orange-600'}`}>
                              {successRate}%
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Total Syncs:</span>
                          <span className="text-gray-900 font-medium">{cp.total_syncs || 0}</span>
                        </div>

                        {cp.last_sync_at && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Last Sync:</span>
                            <span className="text-gray-900 text-xs">
                              {new Date(cp.last_sync_at).toLocaleString()}
                            </span>
                          </div>
                        )}

                        {cp.auto_sync_enabled && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Next Sync:
                            </span>
                            <span className="text-xs text-blue-600 font-medium">
                              {formatNextSync(cp.next_sync_at)}
                            </span>
                          </div>
                        )}
                      </div>

                      {cp.error_message && (
                        <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                          {cp.error_message}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleSyncPlugin(cp)}
                          disabled={syncing === cp.id}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                        >
                          <RefreshCw className={`w-4 h-4 ${syncing === cp.id ? 'animate-spin' : ''}`} />
                          Sync
                        </button>

                        <button
                          onClick={() => handleOpenSettings(cp)}
                          className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </button>

                        <button
                          onClick={() => handleViewHistory(cp)}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <History className="w-4 h-4" />
                          History
                        </button>

                        <button
                          onClick={() => handleDisconnectPlugin(cp)}
                          className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                          Disconnect
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Plug className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No plugins connected yet</p>
              </div>
            )}
          </div>

          {availablePlugins.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Plugins</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {availablePlugins.map(plugin => (
                  <div
                    key={plugin.id}
                    className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col items-center text-center mb-4">
                      <div className={`w-16 h-16 ${getPlatformLogo(plugin.icon).bg} rounded-xl flex items-center justify-center shadow-lg mb-3`}>
                        {getPlatformLogo(plugin.icon).logo}
                      </div>
                      <h3 className="font-semibold text-gray-900">{plugin.name}</h3>
                      <p className="text-xs text-gray-600 mt-1">{plugin.description}</p>
                    </div>
                    <button
                      onClick={() => handleConnectPlugin(plugin)}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Connect
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Connect Modal */}
      {showConnectModal && selectedPlugin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Connect {selectedPlugin.name}
              </h3>
              <button onClick={() => setShowConnectModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {selectedPlugin.config_schema.fields.map(field => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type={field.type === 'password' ? 'password' : 'text'}
                    value={credentials[field.name] || ''}
                    onChange={(e) => setCredentials({ ...credentials, [field.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                </div>
              ))}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sync Frequency
                </label>
                <select
                  value={settings.syncFrequency}
                  onChange={(e) => setSettings({ ...settings, syncFrequency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="realtime">Real-time (5 min)</option>
                  <option value="every_15_minutes">Every 15 minutes</option>
                  <option value="every_30_minutes">Every 30 minutes</option>
                  <option value="hourly">Hourly</option>
                  <option value="every_4_hours">Every 4 hours</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="autoSync"
                  checked={settings.autoSyncEnabled}
                  onChange={(e) => setSettings({ ...settings, autoSyncEnabled: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="autoSync" className="text-sm text-gray-700">
                  Enable automatic syncing
                </label>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowConnectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConnection}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && selectedClientPlugin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedClientPlugin.plugin.name} Settings
              </h3>
              <button onClick={() => setShowSettingsModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sync Frequency
                </label>
                <select
                  value={settings.syncFrequency}
                  onChange={(e) => setSettings({ ...settings, syncFrequency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="realtime">Real-time (5 min)</option>
                  <option value="every_15_minutes">Every 15 minutes</option>
                  <option value="every_30_minutes">Every 30 minutes</option>
                  <option value="hourly">Hourly</option>
                  <option value="every_4_hours">Every 4 hours</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="autoSyncSettings"
                  checked={settings.autoSyncEnabled}
                  onChange={(e) => setSettings({ ...settings, autoSyncEnabled: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="autoSyncSettings" className="text-sm text-gray-700">
                  Enable automatic syncing
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedClientPlugin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedClientPlugin.plugin.name} Sync History
              </h3>
              <button onClick={() => setShowHistoryModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {syncHistory.length > 0 ? (
                <div className="space-y-3">
                  {syncHistory.map((log) => (
                    <div key={log.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {log.status === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <span className="font-medium capitalize">{log.sync_type} Sync</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          log.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {log.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Items synced: <span className="font-medium text-gray-900">{log.items_synced}</span></div>
                        <div>Started: <span className="font-medium text-gray-900">{new Date(log.started_at).toLocaleString()}</span></div>
                        {log.completed_at && (
                          <div>Duration: <span className="font-medium text-gray-900">
                            {Math.round((new Date(log.completed_at).getTime() - new Date(log.started_at).getTime()) / 1000)}s
                          </span></div>
                        )}
                        {log.error_details && (
                          <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-800">
                            {log.error_details.message}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No sync history available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PluginManagerEnhanced;
